import type { PortalMeResponseDto } from '@supply-mover-2-0/shared';
import { authFetch, portalFetch } from '../lib/apiClient';

// --- Login server types (not shared DTOs since these are login-server-specific) ---

export interface TenantCheckResponse {
	valid: boolean;
	name: string;
	authOptions?: {
		phoneLoginEnabled: boolean;
	};
}

export interface PortalLoginResponse {
	accessToken: string;
	refreshToken: string;
	expiresIn: number;
	publicUserId: number;
	tenantAlias: string;
	languagePreference: string;
}

export interface SmsLoginRequestResponse {
	success: boolean;
	/** Seconds until the code expires (900 = 15 minutes) */
	expiresIn: number;
}

/**
 * Check whether a tenant alias maps to an active instance.
 * Returns the instance name for display and available auth options.
 * Throws on 404 (not found).
 */
export async function checkTenant(alias: string): Promise<TenantCheckResponse> {
	return authFetch<TenantCheckResponse>(`/tenant/${encodeURIComponent(alias)}`);
}

/**
 * Authenticate a portal user with email/password. Calls the login server.
 *
 * The login server derives the brand/company from the request hostname
 * (Origin/Referer/Host) and looks up the portal_site row matching that
 * alias. The frontend never needs to send a brand identifier — every
 * storefront URL on a given origin maps to exactly one company.
 */
export async function login(
	email: string,
	password: string,
	tenantAlias: string,
): Promise<PortalLoginResponse> {
	return authFetch<PortalLoginResponse>('/login', {
		method: 'POST',
		body: JSON.stringify({ email, password, tenantAlias }),
	});
}

/**
 * Request an SMS verification code for passwordless login.
 * Always returns success to prevent phone number enumeration.
 */
export async function requestSmsCode(
	phoneNumber: string,
	tenantAlias: string,
): Promise<SmsLoginRequestResponse> {
	return authFetch<SmsLoginRequestResponse>('/sms-login/request', {
		method: 'POST',
		body: JSON.stringify({ phoneNumber, tenantAlias }),
	});
}

/**
 * Verify an SMS code and authenticate.
 * Returns the same token response as password-based login.
 *
 * Brand/company is derived server-side from the request hostname; no
 * client-supplied identifier needed.
 */
export async function verifySmsCode(
	phoneNumber: string,
	code: string,
	tenantAlias: string,
): Promise<PortalLoginResponse> {
	return authFetch<PortalLoginResponse>('/sms-login/verify', {
		method: 'POST',
		body: JSON.stringify({ phoneNumber, code, tenantAlias }),
	});
}

/**
 * Fetch the currently authenticated portal user profile, clients, and active client.
 */
export async function me(token: string): Promise<PortalMeResponseDto> {
	return portalFetch<PortalMeResponseDto>('/auth/me', token);
}

/**
 * Set the active client for the current portal session.
 * Returns the confirmed activeClientId.
 */
export async function selectClient(
	token: string,
	clientId: number,
): Promise<{ activeClientId: number }> {
	return portalFetch<{ activeClientId: number }>('/auth/select-client', token, {
		method: 'POST',
		body: JSON.stringify({ clientId }),
	});
}
